import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
    FileDownloadOutlined,
    CheckBoxOutlineBlankOutlined,
    CheckBoxOutlined,
} from "@mui/icons-material";
import {
    Autocomplete,
    TextField,
    Chip,
    Checkbox,
    IconButton,
    Tooltip,
    Box,
} from "@mui/material";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import DateRangeToggle from "components/Shared/DateRangeToggle";
import { exportExcelFile } from "utils/exportExcelFile";
import moment from "moment";
import { useLazyListVendorShipmentsQuery } from "store/services/vendorProfileApi";
import { useVendorProfileDetailContext } from "contexts/VendorProfileDetailContext";

const EXCEL_DATA_INITIAL_STATE = {
    dateRange: [],
    statuses: [],
};

const SHIPMENT_STATUSES = [
    "Pending",
    "Collected",
    "Consolidation",
    "Loaded",
    "Unloaded",
    "Deconsolidation",
    "CrossDocked",
    "SendOut",
    "Warehousing",
    "Delivered",
    "DeliveryFailed",
    "Return",
    "Cancelled",
];

const ExportVendorShipment = () => {
    const [showDialog, setShowDialog] = useState(false);
    const {
        control,
        handleSubmit,
        reset,
    } = useForm({
        defaultValues: EXCEL_DATA_INITIAL_STATE,
    });

    const [fetchShipments] = useLazyListVendorShipmentsQuery();
    const { vendorProfileDetail } = useVendorProfileDetailContext();

    const handleDownload = async (data) => {
        const { dateRange, statuses } = data;

        const fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
        const toDate = moment(dateRange[1]).format("YYYY-MM-DD");

        const payload = {
            vendorId: vendorProfileDetail._id,
            ...(statuses.length > 0 && { statuses }),
            fromDate,
            toDate,
        };

        try {
            const responseData = await fetchShipments(payload).unwrap();

            if (!responseData?.data || responseData.data.length === 0) {
                toast.info(
                    "No shipment records found for the selected date range and filters."
                );
                return;
            }

            const formattedData = responseData.data.map((row, index) => ({
                SN: index + 1,
                Vendor: row.vendor?.shopName || "N/A",
                "Shipment ID": row.shipmentId || "N/A",
                "Source Branch": row.sourceBranch?.name || "N/A",
                "Destination Branch": row.destinationBranch?.name || "N/A",
                Receiver: row.receiver?.customerUser?.name || "N/A",
                "Shipment Status": row.status || "N/A",
                CreatedAt: row.createdAt
                    ? moment(row.createdAt).format("YYYY-MM-DD HH:mm:ss")
                    : "N/A",
            }));

            exportExcelFile({
                sheetData: formattedData,
                fileName: `Vendor_Shipment_List_${moment().format("YYYYMMDD")}`,
            });

            if (responseData.pagination?.total > responseData.data.length) {
                toast.info(
                    `Downloaded ${responseData.data.length} of ${responseData.pagination.total} records. ` +
                    `Some records were not included due to the maximum limit.`
                );
            } else {
                toast.success("Data downloaded successfully");
            }
        } catch (error) {
            console.error("Error fetching shipment data:", error);
            toast.error("Error fetching shipment data: " + (error.message || "Unknown error"));
        }
        handleReset();
    };

    const handleReset = () => {
        setShowDialog(false);
        reset(EXCEL_DATA_INITIAL_STATE);
    };

    return (
        <>
            <Tooltip title="Export Vendor Shipments" arrow>
                <IconButton color="success" onClick={() => setShowDialog(true)}>
                    <FileDownloadOutlined />
                </IconButton>
            </Tooltip>

            <AppConfirmDialog
                open={showDialog}
                onClose={handleReset}
                onConfirm={handleSubmit(handleDownload)}
                title="Download Vendor Shipment Excel"
                dialogIcon={() => <FileDownloadOutlined color="success" />}
                sx={{ "& .MuiDialog-paper": { maxWidth: "600px", width: "100%" } }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 1 }}>
                    <Controller
                        name="statuses"
                        control={control}
                        render={({ field }) => (
                            <Autocomplete
                                multiple
                                options={SHIPMENT_STATUSES}
                                value={field.value}
                                onChange={(_, value) => field.onChange(value)}
                                disableCloseOnSelect
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Filter by Status"
                                        size="small"
                                        sx={{ "& .MuiInputBase-root": { fontSize: "0.875rem" } }}
                                    />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            label={option}
                                            size="small"
                                            {...getTagProps({ index })}
                                            sx={{
                                                m: 0.5,
                                                height: 24,
                                                "& .MuiChip-label": { fontSize: "0.75rem" },
                                            }}
                                        />
                                    ))
                                }
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox
                                            checked={selected}
                                            icon={<CheckBoxOutlineBlankOutlined fontSize="small" />}
                                            checkedIcon={<CheckBoxOutlined fontSize="small" />}
                                            sx={{ p: 0.5 }}
                                        />
                                        <span style={{ fontSize: "0.875rem" }}>{option}</span>
                                    </li>
                                )}
                                sx={{ width: "100%" }}
                                ListboxProps={{ style: { maxHeight: 200 } }}
                            />
                        )}
                    />

                    <Controller
                        name="dateRange"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <DateRangeToggle
                                {...field}
                                label="Select Date Range"
                                size="small"
                            />
                        )}
                    />
                </Box>
            </AppConfirmDialog>
        </>
    );
};

export default ExportVendorShipment;